/**
 * LoomSelection — Variant C unified section-picker (Spec 19 v2).
 *
 * The surface the writer lands on AFTER choosing a doc-type +
 * library in LoomFresh, but BEFORE the run starts. Top to bottom:
 *
 *   ‹ Pick sections to draft          title row; the chevron = Cancel
 *   [CSR] [ICH E3] [Region: EU]        doctype chips
 *   [Search sections or numbers]       filters by number or title
 *   All · 30 | Not drafted · 24 | Drafted · 6   (only once something is drafted)
 *   ☐ Select all       Select all not drafted   → "3 of 24 selected  Clear"
 *   GATE NAME                 2/23 ⌄   collapsible group
 *     ☐ 5   Ethics            ~1m     titles wrap; drafted rows say so
 *   footer: steering instructions (collapsible), "N sections selected ·
 *   est. ~X min", Cancel + "Draft N sections" / "Redraft N sections".
 *
 * Sections stay grouped by gate (Spec 23 §4); dependencies auto-resolve
 * (parents + deps pulled in and marked "dep · needed by §N"). A dep is an
 * ordinary checkbox: unticking it leaves it (and deps only it needed) out.
 *
 * A new document is named (and filed in a folder) in the footer, in a "Save
 * this document as" block with a suggested name, so naming happens at the
 * moment of commitment instead of in a pop-up earlier in the flow. When
 * drafting more into an existing document the block only says which document
 * the sections go into.
 *
 * SRP: presentation + local interaction state only. All data flows
 * in via props; the ``onStart`` callback hands the resolved
 * selection back to the shell, which is responsible for converting
 * section ids to backend ``module_ids`` (or ``gates``) and calling
 * ``startRun`` with the optional ``plan_id`` when show-plan is on.
 *
 * Accessibility:
 *   - status filter is a tablist; group headers are buttons with
 *     ``aria-expanded``
 *   - every row wraps a native checkbox (visually hidden) so keyboard
 *     and screen readers keep checkbox semantics
 *
 * # DECISION: no write-strategy toggle — every run is live fill.
 * # WHY: the old Live/Batched toggle was preview-only (the backend
 * #      honours ``generation.insertion_mode``), and Guru confirmed all
 * #      runs are live fill now, so the choice only confused writers.
 * # DATE: 2026-10-14
 *
 * # DECISION: first draft opens with every section ticked; "Draft more"
 * #           (``defaultEmpty``) opens with nothing ticked.
 * # WHY: on a fresh document "draft the whole thing" is the common intent.
 * #      Once part of it is drafted, pre-ticking every remaining section
 * #      made one click start a 20+ section run; the design feedback
 * #      (2026-10-16) opens empty with a one-click "Select all not drafted".
 * # DATE: 2026-10-16
 *
 * # DECISION: an already-drafted parent is not auto-included, and a
 * #           re-drafted section pulls in nothing.
 * # WHY: ticking §5.3 implied §5; when §5 was drafted that silently
 * #      re-drafted (overwrote) it. A drafted parent's heading is already
 * #      in the document, and re-drafting §5.3 replaces only §5.3. The
 * #      writer can still tick the parent to re-draft it on purpose.
 * # DATE: 2026-10-16
 *
 * # DECISION: pulled-in dependencies come in ticked but can be unticked.
 * # WHY: the product owner approved (prototype, 2026-10-19) pre-ticking a
 * #      section's declared dependencies so §15 is not drafted against
 * #      "not yet drafted" placeholders, while leaving the writer in
 * #      control: an unticked dep goes into ``skipped`` and the implied set
 * #      is recomputed as the closure of checked minus skipped. Ticking a
 * #      section clears the skips in its own closure. Dotted parents
 * #      (§9.3 → §9) stay fixed as before. No dialog, no new screen.
 * # DATE: 2026-10-19
 *
 * # DECISION: the overwrite confirmation stays even with the Drafted-tab note.
 * # WHY: the add-in keeps no per-section history, so a re-draft cannot be
 * #      undone from the pane; the note warns early, the confirm is the gate.
 * # DATE: 2026-10-16
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type LeafOption, groupLeaves } from "../leafDocuments";
import {
  type GateMeta,
  type SelectionNode,
  type WriteStrategy,
  descendantsOf,
  estMinutes,
  groupByGate,
  resolveSelection,
  selectionClosure,
} from "../selection";
import { BackLink, Button, FooterBar, Segmented, SearchField, Tag } from "../../ds/components";
import { brandColors } from "../tokens";
import { formatAcceptedAt } from "./LoomSpine";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** What the shell receives when the writer commits the picker. The
 *  shell is responsible for mapping ``resolvedSectionIds`` to
 *  backend module_ids (via DocTypeConfig.modules) and calling
 *  createPlan / startRun accordingly. */
export interface LoomSelectionCommit {
  /** Section ids in tree order — union of checked + implied. */
  resolvedSectionIds: string[];
  /** Subset of ``resolvedSectionIds`` the writer explicitly ticked.
   *  Useful when the shell wants to present "you picked N, we added
   *  K deps" copy in a downstream surface. */
  explicitSectionIds: string[];
  /** Writer's write-strategy choice. Preview-only in v1 — the
   *  shell may still use this for post-run UX hints. */
  strategy: WriteStrategy;
  /** When true the writer wants a plan-review surface before the
   *  run starts; when false they've chosen to skip plan review and
   *  start drafting immediately. */
  showPlan: boolean;
  /** The writer's final comments for this batch, trimmed. Sent as the run's
   *  ``run_instructions`` and appended to every selected section's
   *  instructions. Absent when the box was left empty. */
  runInstructions?: string;
  /** The new document's name, trimmed. Present only when ``naming`` was
   *  ``mode: "new"``. */
  name?: string;
  /** Where to file the new document: a listed folder (``""`` = Unfiled) or a
   *  new folder to create first. Present only alongside ``name`` when the
   *  picker offered folders. */
  folder?: LoomFolderPick;
  /** The folder id the shell resolved ``folder`` to (after creating a new
   *  one). Set by the shell, never by the picker. */
  folderId?: string | null;
}

/** A folder picked in the "Save this document as" block. */
export type LoomFolderPick = { value: string } | { newLabel: string };

/** What the footer's "Save this document as" block shows. */
export type LoomNaming =
  | {
      mode: "new";
      /** Pre-filled name, e.g. "CSR · US · 17 Oct". */
      suggestedName: string;
      /** Folder choice; omitted (no row) while the folders are loading. */
      folders?: { options: Array<{ value: string; label: string }>; initial: string };
    }
  | {
      /** "Draft more": the document already has its name and folder. */
      mode: "existing";
      name?: string;
      folderLabel?: string;
    };

/** What the pane already knows about a drafted section, shown on its row. */
export interface DraftedSectionMeta {
  /** Epoch ms when the section's draft landed. */
  acceptedAt?: number;
  /** Unresolved review findings on the draft. */
  issueCount?: number;
}

export interface LoomSelectionProps {
  /** Flat tree from ``buildSelectionTree(docConfig)``. */
  tree: SelectionNode[];
  /** Gate metadata from ``buildGateMeta(docConfig)``. Used for
   *  group headers; order must match ``config.gates``. */
  gates: GateMeta[];
  /** Display code for the header — "CSR", "CER", "PSUR/PBRER". */
  docCode: string;
  /** Secondary header label — "ICH E3", or "ICH E3 · Region: EU". Each
   *  " · "-separated part renders as its own chip. */
  docStandard: string;
  /** Backend's ``generation.insertion_mode`` for this doctype.
   *  Null / undefined → default "live". */
  initialStrategy?: WriteStrategy;
  /** Initial checked set — defaults to every section (or none with
   *  ``defaultEmpty``). */
  initialChecked?: string[];
  /** Open with nothing ticked when ``initialChecked`` is absent — the
   *  "Draft more" entry, where part of the document is already drafted. */
  defaultEmpty?: boolean;
  /** Non-actionable section ids (already drafted / locked). */
  lockedSectionIds?: string[];
  /** Already-drafted section ids that remain SELECTABLE — ticking one
   *  re-drafts (overwrites) it. Shown as drafted, listed under the Drafted
   *  tab and defaulted OFF, but (unlike ``lockedSectionIds``) NOT excluded
   *  from toggling or the commit. */
  draftedSectionIds?: string[];
  /** Per drafted section id: accepted time + open issues, for the row line. */
  draftedMeta?: Readonly<Record<string, DraftedSectionMeta>>;
  /** The eCTD leaves this doctype offers, or `null` when it is filed as ONE
   *  document — CSR, PROTOCOL_DRUG, PSUR and most others. `null` is the
   *  load-bearing value: the leaf step renders for nobody else, so those
   *  doctypes see exactly the picker they saw before leaves existed. */
  leaves?: LeafOption[] | null;
  /** Which leaf this document IS, once chosen. */
  leafDocumentId?: string | null;
  /** Writer picked a leaf. The shell persists the identity and re-scopes the
   *  tree; this component holds no leaf state of its own. */
  onLeafSelect?: (leafDocumentId: string) => void;
  /** Writer clicked the primary footer button. */
  onStart: (commit: LoomSelectionCommit) => void;
  /** Writer clicked Cancel (or the back chevron) — shell routes back to LoomFresh. */
  onCancel: () => void;
  /** The "Save this document as" block above the buttons. Absent → no block. */
  naming?: LoomNaming;
  /** Extra header tags after the doctype ones, e.g. "US · FDA", the library. */
  extraTags?: readonly string[];
  /** Header step label, e.g. "New document · step 5 of 5". */
  stepLabel?: string;
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Decide the primary footer button copy. Pure so the grammar is
 * testable. ``selectedCount`` is the number of sections the run will
 * actually draft (checked + implied, grouping headers excluded).
 *
 *   - selectedCount === 0                 → "Select sections to draft"
 *   - showPlan                            → "Generate plan"
 *   - every selected section is drafted   → "Redraft N sections"
 *   - strategy==='batched'                → "Start batched run · N sections"
 *   - otherwise                           → "Draft N sections"
 */
export function formatPrimaryLabel(
  selectedCount: number,
  showPlan: boolean,
  strategy: WriteStrategy,
  redraftOnly = false,
): string {
  if (selectedCount === 0) return "Select sections to draft";
  if (showPlan) return "Generate plan";
  if (redraftOnly) return `Redraft ${plural(selectedCount, "section")}`;
  if (strategy === "batched") return `Start batched run · ${plural(selectedCount, "section")}`;
  return `Draft ${plural(selectedCount, "section")}`;
}

/**
 * Footer summary beside the estimate — "3 sections selected", with the
 * auto-included and re-draft counts only when they apply.
 */
export function formatSelectionSummary(draftCount: number, impliedCount: number, redraftCount: number): string {
  const parts: string[] = [];
  if (impliedCount > 0) parts.push(`${impliedCount} added as dep${impliedCount === 1 ? "" : "s"}`);
  if (redraftCount > 0 && redraftCount < draftCount)
    parts.push(`${redraftCount} redraft${redraftCount === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/** The quiet meta line on a pulled-in row: "dep · needed by §15". */
export function formatDepLine(neededBy: string | undefined): string {
  return neededBy ? `dep · needed by §${neededBy}` : "dep";
}

/** Does a row match the search box? Number prefix ("9.3" → 9.3, 9.3.1)
 *  or a case-insensitive title substring. Empty query matches all. */
export function matchesQuery(node: Pick<SelectionNode, "n" | "t">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return node.n.toLowerCase().startsWith(q) || node.t.toLowerCase().includes(q);
}

/**
 * Build the commit payload for ``onStart`` from the picker's resolved
 * state. Pure — the primary button and the "skip plan" link share
 * this helper so the contract ("which ids go out, in what order, with
 * which strategy + plan flag") has one source of truth and one place
 * to test.
 *
 * Guarantees:
 *   - Tree order preserved in both resolvedSectionIds and
 *     explicitSectionIds (downstream grouping relies on it).
 *   - resolvedSectionIds = checked ∪ implied.
 *   - explicitSectionIds = checked only (subset of resolved).
 *   - strategy + withPlan threaded through verbatim.
 */
export function buildCommitPayload(
  tree: SelectionNode[],
  checkedSet: ReadonlySet<string>,
  impliedSet: ReadonlySet<string>,
  strategy: WriteStrategy,
  withPlan: boolean,
  runInstructions = "",
): LoomSelectionCommit {
  // Grouping headers (drafts:false) own no module and are never drafted;
  // they shouldn't reach the backend even if one somehow slipped into a
  // set. Belt-and-suspenders — headers are already kept out of
  // checked/implied by the picker.
  const resolvedSectionIds = tree
    .filter((n) => n.drafts !== false && (checkedSet.has(n.n) || impliedSet.has(n.n)))
    .map((n) => n.n);
  const explicitSectionIds = tree.filter((n) => n.drafts !== false && checkedSet.has(n.n)).map((n) => n.n);
  const comments = runInstructions.trim();
  return {
    resolvedSectionIds,
    explicitSectionIds,
    strategy,
    showPlan: withPlan,
    ...(comments ? { runInstructions: comments } : {}),
  };
}

/** Matches the backend cap on ``run_instructions``. */
export const RUN_INSTRUCTIONS_MAX_CHARS = 4000;

type StatusTab = "all" | "todo" | "done";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// Visually hidden native input — keeps the checkbox semantics and
// keyboard behaviour while the styled box carries the look.
const hiddenInput: React.CSSProperties = { position: "absolute", opacity: 0, width: 0, height: 0, margin: 0 };

// Control chrome, from the design system (board "06 New document · step 4").
const neutral = {
  fill: "var(--surface-sunken)",
  line: "var(--line)",
  lineStrong: "var(--line-strong)",
  disabled: "var(--line-strong)",
} as const;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    background: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-sans)",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "16px 16px 12px",
    borderBottom: "1px solid var(--line)",
    flexShrink: 0,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: "4px" },
  docRow: { display: "flex", alignItems: "center", gap: "8px" },
  docLabel: { fontSize: "12px", color: "var(--ink-muted)", flexShrink: 0 },
  docPicker: { flex: 1, minWidth: 0 },
  selectRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
  },
  selectAllLabel: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 500,
    cursor: "pointer",
  },
  selectLinks: { display: "flex", gap: "12px" },
  textButton: {
    border: "none",
    background: "none",
    padding: 0,
    color: "var(--brand-strong)",
    font: "500 12px var(--font-sans)",
    cursor: "pointer",
    ":hover": { color: "var(--brand)" },
    ":disabled": { color: "var(--ink-subtle)", cursor: "default" },
  },
  body: { flex: 1, minHeight: 0, overflowY: "auto" },
  group: {},
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "8px 12px",
    border: "none",
    borderBottom: "1px solid var(--line)",
    background: "var(--surface-sunken)",
    color: "var(--ink-muted)",
    font: "600 11px/16px var(--font-sans)",
    letterSpacing: "0.06em",
    textAlign: "left",
    cursor: "pointer",
  },
  groupLabel: { flex: 1, minWidth: 0, textTransform: "uppercase" },
  groupCount: { fontVariantNumeric: "tabular-nums" },
  row: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "16px auto 1fr auto",
    columnGap: "8px",
    alignItems: "start",
    padding: "8px 12px",
    borderBottom: "1px solid var(--line)",
    fontSize: "13px",
    lineHeight: "20px",
    background: "var(--surface)",
  },
  rowSelected: { background: "var(--brand-soft)" },
  checkboxBox: {
    width: "16px",
    height: "16px",
    marginTop: "2px",
    borderRadius: "4px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  rowNumber: { font: "500 12px/20px var(--font-mono)", whiteSpace: "nowrap" },
  rowBody: { display: "flex", flexDirection: "column", minWidth: 0 },
  rowTitle: { overflowWrap: "anywhere" },
  rowMeta: { fontSize: "12px", lineHeight: "16px", color: "var(--ink-muted)" },
  rowEst: { font: "400 12px/20px var(--font-mono)", color: "var(--ink-subtle)", whiteSpace: "nowrap" },
  redraftNote: {
    margin: "12px",
    padding: "10px 12px",
    borderRadius: "var(--radius-md)",
    background: "var(--warning-soft, var(--surface-sunken))",
    border: "1px solid var(--line)",
    fontSize: "12px",
    lineHeight: "16px",
  },
  empty: { padding: "24px 16px", fontSize: "13px", color: "var(--ink-muted)", textAlign: "center" },
  awaitingLeaf: { flex: 1, padding: "24px 16px", fontSize: "13px", color: "var(--ink-muted)", textAlign: "center" },
  optional: { fontWeight: 400, color: "var(--ink-subtle)" },
  steerHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    padding: "2px 0",
    border: "none",
    background: "none",
    textAlign: "left",
    font: "600 12.5px/16px var(--font-sans)",
    color: "var(--ink)",
    cursor: "pointer",
    "&:focus-visible": { outline: "2px solid var(--brand)", outlineOffset: "2px" },
  },
  extras: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "12px 16px",
    background: "var(--surface)",
    borderTop: "1px solid var(--line)",
    flex: "none",
    // Pinned, so cap it: the section list keeps most of a short pane.
    maxHeight: "45%",
    overflowY: "auto",
  },
  saveAs: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--line)",
    background: "var(--brand-wash)",
  },
  saveAsHead: { display: "flex", justifyContent: "space-between", gap: "8px", fontSize: "12px", fontWeight: 600 },
  saveAsHint: { fontWeight: 400, color: "var(--ink-muted)" },
  saveAsFolder: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "var(--ink-muted)" },
  saveAsLink: {
    alignSelf: "flex-start",
    border: "none",
    background: "none",
    padding: 0,
    color: "var(--brand-strong)",
    font: "500 12px var(--font-sans)",
    cursor: "pointer",
  },
  saveAsRowFolder: { fontWeight: 400, color: "var(--ink-muted)" },
  confirmOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    background: "rgba(28, 21, 39, 0.32)",
  },
  confirmCard: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "var(--radius-lg)",
    background: "var(--surface-raised)",
    border: "1px solid var(--line)",
    boxShadow: "var(--shadow-overlay)",
  },
  confirmTitle: { fontSize: "15px", fontWeight: 600 },
  confirmBody: { fontSize: "13px", lineHeight: "20px", color: "var(--ink-muted)" },
  confirmButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
  },
});

// ---------------------------------------------------------------------------
// Glyphs
// ---------------------------------------------------------------------------

/** SVG checkmark — shared by the select-all and row checkboxes. */
const Checkmark: React.FC<{ color?: string }> = ({ color = "var(--on-brand)" }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <path
      d="M2 5 L4.2 7 L8 3"
      stroke={color}
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Small padlock glyph shown in a locked row's checkbox — marks a
 *  section as non-selectable. */
const LockIcon: React.FC<{ color?: string }> = ({ color = "#fff" }) => (
  <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
    <rect x="2" y="4.5" width="6" height="4.5" rx="0.8" fill={color} />
    <path
      d="M3.2 4.5 V3 a1.8 1.8 0 0 1 3.6 0 v1.5"
      stroke={color}
      strokeWidth="1.1"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

/** Chevron — "left" for back, "down"/"up" for collapsibles. */
const Chevron: React.FC<{ dir: "left" | "down" | "up"; size?: number }> = ({ dir, size = 14 }) => {
  const d = dir === "left" ? "M9 3 L5 7 L9 11" : dir === "down" ? "M3 5.5 L7 9.5 L11 5.5" : "M3 9 L7 5 L11 9";
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true">
      <path d={d} stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const SearchIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <path d="M9.2 9.2 L12.5 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

type BoxState = "checked" | "some" | "implied" | "locked" | "unchecked";

/** The styled checkbox square in the brand primary (violet for Asthra,
 *  blue for the Indegene white-label). Purely visual — the row's hidden
 *  native input carries the semantics. */
const CheckBox: React.FC<{ state: BoxState }> = ({ state }) => {
  const styles = useStyles();
  const c = brandColors.primary;
  return (
    <span
      className={styles.checkboxBox}
      aria-hidden="true"
      style={{
        border: state === "unchecked" ? `1.5px solid ${neutral.lineStrong}` : `1.5px solid ${c}`,
        background:
          state === "checked"
            ? c
            : state === "some"
              ? c
              : state === "implied"
                ? brandColors.light
                : state === "locked"
                  ? neutral.disabled
                  : "var(--surface)",
      }}
    >
      {state === "checked" && <Checkmark />}
      {state === "locked" && <LockIcon color="var(--ink-muted)" />}
      {state === "some" && <span style={{ width: "8px", height: "1.6px", background: "var(--on-brand)" }} />}
      {state === "implied" && <span style={{ width: "6px", height: "6px", borderRadius: "1px", background: c }} />}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// The folder select's "+ New folder…" entry; never a real folder id.
const NEW_FOLDER = "__new_folder__";

export const LoomSelection: React.FC<LoomSelectionProps> = ({
  tree,
  gates,
  docCode,
  docStandard,
  initialStrategy = "live",
  initialChecked,
  defaultEmpty = false,
  lockedSectionIds,
  draftedSectionIds,
  draftedMeta,
  leaves = null,
  leafDocumentId = null,
  onLeafSelect,
  onStart,
  onCancel,
  naming,
  stepLabel = "New document · step 5 of 5",
  extraTags,
}) => {
  const styles = useStyles();
  // "Save this document as" — only for a new document.
  const newDoc = naming?.mode === "new" ? naming : null;
  const [docName, setDocName] = useState(newDoc?.suggestedName ?? "");
  // The writer's folder pick; until they pick, follow the shell's initial
  // (folders can arrive after the picker mounts).
  const [folderPicked, setFolder] = useState<string | null>(null);
  const folder = folderPicked ?? newDoc?.folders?.initial ?? "";
  // Non-null while "+ New folder" is open: the new folder's name.
  const [newFolder, setNewFolder] = useState<string | null>(null);
  // The block starts as one bordered row; Change opens the name and folder.
  const [editingName, setEditingName] = useState(false);
  const lockedSet = useMemo(() => new Set(lockedSectionIds ?? []), [lockedSectionIds]);
  // Already-drafted-but-overwritable rows. Selectable and included in the
  // commit when ticked, but defaulted OFF so "Draft more" never silently
  // overwrites prior work.
  const draftedSet = useMemo(() => new Set(draftedSectionIds ?? []), [draftedSectionIds]);
  // Section id → node, for O(1) lookups (drafts flag, header cascade).
  const byId = useMemo(() => new Map(tree.map((n) => [n.n, n])), [tree]);
  const isDraftable = useCallback((n: SelectionNode) => n.drafts !== false, []);

  // Default check = EVERY draftable node (top-level + subsections), since
  // drafting subsections requires explicit ticks (the resolver
  // auto-includes parents but not children). "Draft more" opens empty.
  // biome-ignore lint/correctness/useExhaustiveDependencies: stable default computed once on mount
  const defaultChecked = useMemo(
    () =>
      (initialChecked ?? (defaultEmpty ? [] : tree.map((n) => n.n))).filter(
        // Grouping headers (drafts:false) never seed ``checked`` — their
        // state is derived tri-state from their instructed descendants.
        (id) => !lockedSet.has(id) && !draftedSet.has(id) && byId.get(id)?.drafts !== false,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [checked, setChecked] = useState<string[]>(defaultChecked);
  // Pulled-in dependencies the writer unticked. Only affects implied rows:
  // a skipped dep is left out, and so are deps only it needed.
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(() => new Set());
  // ``defaultChecked`` only filters the drafted rows known at mount. After a
  // re-login the spine can mark sections drafted a beat after the picker opens,
  // which left those rows ticked and walked "Draft more" straight into the
  // overwrite prompt. Untick a row the moment it becomes known as drafted; one
  // the writer ticks after that stays ticked.
  const knownDraftedRef = useRef<ReadonlySet<string>>(draftedSet);
  useEffect(() => {
    const newlyDrafted = [...draftedSet].filter((id) => !knownDraftedRef.current.has(id));
    knownDraftedRef.current = draftedSet;
    if (newlyDrafted.length === 0) return;
    setChecked((prev) => prev.filter((id) => !newlyDrafted.includes(id)));
  }, [draftedSet]);
  const strategy: WriteStrategy = initialStrategy;
  // Show-plan stays off while its toggle is out of the UI; kept so the
  // plan-review path can return without re-plumbing.
  const [showPlan] = useState<boolean>(false);
  // True while the overwrite confirmation is up — Start was pressed with
  // already-drafted sections in the selection. Cancel returns to the picker.
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  // The writer's final comments for this batch — applied to every section.
  const [runInstructions, setRunInstructions] = useState("");
  // The steering box hides behind a link until asked for; once opened it
  // gets a header that folds it (text is kept while folded).
  const [steerShown, setSteerShown] = useState(false);
  const [steerOpen, setSteerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<StatusTab>("all");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  // Derived state — the resolver is cheap but memoising keeps row
  // renders reference-stable across toggles that don't change the
  // underlying sets.
  const { checkedSet } = useMemo(() => resolveSelection(tree, checked), [tree, checked]);
  // Parents/deps are pulled in only for NEW sections: re-drafting a drafted
  // subsection replaces just that section, so its ticks imply nothing. A
  // drafted or locked dep is not walked: its own deps aren't needed again.
  const stopAt = useMemo(() => [...draftedSet, ...lockedSet], [draftedSet, lockedSet]);
  const newChecked = useMemo(() => checked.filter((id) => !draftedSet.has(id)), [checked, draftedSet]);
  const { impliedSet, neededBy, parentImplied } = useMemo(
    () => resolveSelection(tree, newChecked, { skipped, stopAt }),
    [tree, newChecked, skipped, stopAt],
  );
  const effectiveCheckedSet = useMemo(() => {
    const next = new Set<string>();
    for (const id of checkedSet) if (!lockedSet.has(id)) next.add(id);
    return next;
  }, [checkedSet, lockedSet]);
  // Implied parents/deps, minus locked ones, ticked ones and already-drafted
  // ones — a drafted parent is never re-drafted just because a child was ticked.
  const effectiveImpliedSet = useMemo(() => {
    const next = new Set<string>();
    for (const id of impliedSet) if (!lockedSet.has(id) && !draftedSet.has(id) && !checkedSet.has(id)) next.add(id);
    return next;
  }, [impliedSet, lockedSet, draftedSet, checkedSet]);

  const gateLabelByName = useMemo(() => {
    const out = new Map<string, string>();
    for (const g of gates) out.set(g.name, g.displayName);
    return out;
  }, [gates]);

  // -- Filters ------------------------------------------------------------
  const isDone = useCallback((id: string) => draftedSet.has(id) || lockedSet.has(id), [draftedSet, lockedSet]);
  const hasDrafted = draftedSet.size > 0 || lockedSet.size > 0;
  const activeTab: StatusTab = hasDrafted ? tab : "all";
  const inTab = useCallback(
    (n: SelectionNode) =>
      isDraftable(n) && (activeTab === "all" || (activeTab === "done" ? isDone(n.n) : !isDone(n.n))),
    [activeTab, isDone, isDraftable],
  );
  const tabCounts = useMemo(() => {
    const draftable = tree.filter(isDraftable);
    const done = draftable.filter((n) => isDone(n.n)).length;
    return { all: draftable.length, todo: draftable.length - done, done };
  }, [tree, isDone, isDraftable]);

  // Rows that match the tab + search themselves ("hits"), and the rows to
  // render: hits plus their ancestors, so a match keeps its context.
  const hitIds = useMemo(
    () => new Set(tree.filter((n) => inTab(n) && matchesQuery(n, query)).map((n) => n.n)),
    [tree, inTab, query],
  );
  const shownIds = useMemo(() => {
    const out = new Set<string>(hitIds);
    if (activeTab === "done") return out; // flat list, no context rows
    for (const id of hitIds) {
      for (let p = byId.get(id)?.parent; p; p = byId.get(p)?.parent) out.add(p);
    }
    return out;
  }, [hitIds, activeTab, byId]);
  const groups = useMemo(
    () =>
      groupByGate(tree)
        .map((g) => ({ ...g, nodes: g.nodes.filter((n) => shownIds.has(n.n)) }))
        .filter((g) => g.nodes.length > 0),
    [tree, shownIds],
  );

  // -- Handlers ---------------------------------------------------------
  // Toggling a row cascades down to its subsections: ticking a parent
  // auto-ticks every section nested under it, unticking clears them
  // too. Leaf rows toggle just themselves.
  const toggleRow = useCallback(
    (id: string) => {
      if (lockedSet.has(id)) return;
      // A pulled-in dependency: unticking it skips it (and, by the
      // recomputed closure, any deps only it needed).
      if (effectiveImpliedSet.has(id) && !parentImplied.has(id)) {
        setSkipped((prev) => new Set([...prev, id]));
        return;
      }
      // A skipped dep that is still needed: ticking it brings it (and its
      // deps) back as a dep rather than as an explicit tick.
      if (skipped.has(id) && !checked.includes(id)) {
        const unskipped = [...skipped].filter((x) => x !== id);
        if (resolveSelection(tree, newChecked, { skipped: unskipped, stopAt }).impliedSet.has(id)) {
          const clear = new Set([id, ...selectionClosure(tree, id)]);
          setSkipped((prev) => new Set([...prev].filter((x) => !clear.has(x))));
          return;
        }
      }
      const isHeader = byId.get(id)?.drafts === false;
      // A header cascades to its descendants only (it never enters
      // ``checked`` itself); a draftable row toggles itself + descendants.
      const raw = isHeader ? descendantsOf(tree, id) : [id, ...descendantsOf(tree, id)];
      // ``family`` is the set of ids actually written to ``checked`` —
      // draftable + unlocked only, so nested headers never pollute it.
      const family = new Set(raw.filter((fid) => !lockedSet.has(fid) && byId.get(fid)?.drafts !== false));
      if (family.size === 0) return; // header whose only children are locked — inert
      // For a header, "on" means every instructed child is already
      // ticked; toggling then clears them. For a row, its own id.
      const wasChecked = isHeader ? Array.from(family).every((fid) => checked.includes(fid)) : checked.includes(id);
      if (wasChecked) {
        setChecked((prev) => prev.filter((x) => !family.has(x)));
        return;
      }
      setChecked((prev) => Array.from(new Set([...prev, ...family])));
      // Ticking a section wants its whole closure: drop earlier skips in it.
      const clear = new Set<string>();
      for (const fid of family) {
        clear.add(fid);
        for (const c of selectionClosure(tree, fid)) clear.add(c);
      }
      setSkipped((prev) => (prev.size === 0 ? prev : new Set([...prev].filter((x) => !clear.has(x)))));
    },
    [tree, lockedSet, byId, effectiveImpliedSet, parentImplied, skipped, checked, newChecked, stopAt],
  );

  // Select all / its tri-state act on what the writer can see: the current
  // tab, narrowed by the search.
  const visibleActionable = useMemo(() => [...hitIds].filter((id) => !lockedSet.has(id)), [hitIds, lockedSet]);
  const visibleChecked = visibleActionable.filter((id) => effectiveCheckedSet.has(id)).length;
  const allVisibleOn = visibleActionable.length > 0 && visibleChecked === visibleActionable.length;
  const toggleSelectAll = useCallback(() => {
    const ids = new Set(visibleActionable);
    setChecked((prev) => (allVisibleOn ? prev.filter((x) => !ids.has(x)) : Array.from(new Set([...prev, ...ids]))));
  }, [visibleActionable, allVisibleOn]);
  const selectAllNotDrafted = useCallback(
    () => setChecked(tree.filter((n) => isDraftable(n) && !isDone(n.n)).map((n) => n.n)),
    [tree, isDone, isDraftable],
  );
  const clearAll = useCallback(() => {
    setChecked([]);
    setSkipped(new Set());
  }, []);
  const toggleGroup = useCallback(
    (key: string) =>
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    [],
  );

  // Sections the run will actually draft — the same filter
  // ``buildCommitPayload`` applies (checked ∪ implied, headers excluded), so
  // the button count matches what is sent.
  const draftNodes = useMemo(
    () => tree.filter((n) => isDraftable(n) && (effectiveCheckedSet.has(n.n) || effectiveImpliedSet.has(n.n))),
    [tree, effectiveCheckedSet, effectiveImpliedSet, isDraftable],
  );
  const draftCount = draftNodes.length;
  const estMin = useMemo(() => draftNodes.reduce((sum, n) => sum + estMinutes(n), 0), [draftNodes]);
  // Multi-leaf doctypes (CMC / ICH M4Q) offer a document dropdown; every other
  // doctype has ``leaves === null`` and behaves exactly as before. Until a
  // document is chosen there is nothing to scope, so the section area shows a
  // placeholder and Start is disabled.
  const hasLeaves = leaves !== null && leaves.length > 0;
  const awaitingLeaf = hasLeaves && leafDocumentId == null;
  const selectionOk = draftCount > 0 && !awaitingLeaf;
  const nameOk = !newDoc || docName.trim().length > 0;
  const folderOk = !newDoc?.folders || newFolder === null || newFolder.trim().length > 0;
  const canGo = selectionOk && nameOk && folderOk;

  // Already-drafted sections the run would re-draft (overwrite). Implied rows
  // never are (drafted parents are dropped above), so only ticks count.
  const selectedDrafted = useMemo(
    () => [...effectiveCheckedSet].filter((id) => draftedSet.has(id)),
    [effectiveCheckedSet, draftedSet],
  );
  const redraftOnly = draftCount > 0 && selectedDrafted.length === draftCount;

  /** Commit the selection. Takes an explicit ``withPlan`` so a future
   *  "skip plan" link can share one code path without tripping over
   *  React's async state updates. The payload construction itself is a
   *  pure helper (``buildCommitPayload``) covered by unit tests. */
  const handleCommit = useCallback(
    (withPlan: boolean) => {
      if (!canGo) return;
      const payload = buildCommitPayload(
        tree,
        effectiveCheckedSet,
        effectiveImpliedSet,
        strategy,
        withPlan,
        runInstructions,
      );
      if (newDoc) {
        payload.name = docName.trim();
        if (newDoc.folders) payload.folder = newFolder !== null ? { newLabel: newFolder.trim() } : { value: folder };
      }
      onStart(payload);
    },
    [
      canGo,
      tree,
      effectiveCheckedSet,
      effectiveImpliedSet,
      strategy,
      runInstructions,
      onStart,
      newDoc,
      docName,
      folder,
      newFolder,
    ],
  );

  // Primary button — when the selection includes already-drafted sections,
  // stop and confirm the overwrite first; the confirmation's own button commits.
  const handleStart = useCallback(() => {
    if (!canGo) return;
    if (selectedDrafted.length > 0) {
      setConfirmOverwrite(true);
      return;
    }
    handleCommit(showPlan);
  }, [canGo, selectedDrafted, handleCommit, showPlan]);

  // Confirmation cleared — commit with the current plan choice.
  const handleConfirmOverwrite = useCallback(() => {
    setConfirmOverwrite(false);
    handleCommit(showPlan);
  }, [handleCommit, showPlan]);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  const chips = [docCode, ...docStandard.split(" · "), ...(extraTags ?? [])].map((c) => c.trim()).filter(Boolean);
  const tabDefs: { key: StatusTab; label: string; count: number }[] = [
    { key: "all", label: "All", count: tabCounts.all },
    { key: "todo", label: "Not drafted", count: tabCounts.todo },
    { key: "done", label: "Drafted", count: tabCounts.done },
  ];
  const tabDenominator = activeTab === "all" ? tabCounts.all : activeTab === "done" ? tabCounts.done : tabCounts.todo;
  const tabSelected = tree.filter((n) => inTab(n) && effectiveCheckedSet.has(n.n)).length;
  const anySelected = effectiveCheckedSet.size > 0;
  const summaryExtra = formatSelectionSummary(draftCount, effectiveImpliedSet.size, selectedDrafted.length);
  // Number gutter sized to the longest number in the tree so titles align.
  const numberWidth = `${Math.max(2, ...tree.map((n) => n.n.length)) * 7 + 6}px`;

  const renderRow = (node: SelectionNode, flat: boolean) => {
    const isHeader = node.drafts === false;
    // Header tri-state derives from its instructed, actionable descendants.
    let headerAll = false;
    let headerSome = false;
    const kids = descendantsOf(tree, node.n)
      .map((kid) => byId.get(kid))
      .filter((k): k is SelectionNode => !!k && k.drafts !== false);
    if (isHeader) {
      const actionable = kids.filter((k) => !lockedSet.has(k.n));
      headerAll = actionable.length > 0 && actionable.every((k) => checkedSet.has(k.n));
      headerSome = !headerAll && actionable.some((k) => checkedSet.has(k.n) || effectiveImpliedSet.has(k.n));
    }
    // "2 of 3 drafted" under a parent whose subsections are partly drafted.
    const kidsDone = kids.filter((k) => isDone(k.n)).length;
    const parentSummary = !flat && kids.length > 0 && kidsDone > 0 ? `${kidsDone} of ${kids.length} drafted` : null;
    const drafted = draftedSet.has(node.n) || lockedSet.has(node.n);
    const meta = draftedMeta?.[node.n];
    const draftedLine =
      drafted && activeTab === "done"
        ? [
            meta?.acceptedAt ? formatAcceptedAt(meta.acceptedAt) : "drafted",
            meta?.issueCount ? plural(meta.issueCount, "issue") : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : null;
    return (
      <PickerRow
        key={node.n}
        node={node}
        flat={flat}
        checked={checkedSet.has(node.n)}
        implied={effectiveImpliedSet.has(node.n)}
        impliedFixed={parentImplied.has(node.n)}
        neededBy={neededBy.get(node.n)}
        locked={lockedSet.has(node.n)}
        drafted={drafted}
        isHeader={isHeader}
        headerAll={headerAll}
        headerSome={headerSome}
        subline={draftedLine ?? parentSummary}
        bold={isHeader || (!flat && kids.length > 0)}
        numberWidth={numberWidth}
        showDraftedTag={drafted && !draftedLine}
        onToggle={toggleRow}
      />
    );
  };

  return (
    <div className={mergeClasses("as-takeover", styles.root)}>
      <div className={styles.header}>
        <div className="as-stephead-top">
          <BackLink onClick={onCancel}>Back</BackLink>
          <span className="as-stephead-step">{naming?.mode === "existing" ? "Draft more" : stepLabel}</span>
        </div>
        <h1 className="as-stephead-title">Pick sections to draft</h1>
        <div className={styles.chips}>
          {chips.map((c, i) => (
            <Tag key={c} tone={i === 0 ? "brand" : undefined}>
              {c}
            </Tag>
          ))}
        </div>

        {/* WHICH document. CTD Module 2 is filed as 15 separate eCTD documents
            and Module 3 as 65, so those doctypes pick a document before its
            sections. `leaves` is null for every single-document doctype, so this
            renders for nobody else. Picking one re-scopes the section list
            below via the shell. */}
        {hasLeaves && (
          <div className={styles.docRow}>
            <span className={styles.docLabel}>Document</span>
            <select
              className={mergeClasses("as-input as-select", styles.docPicker)}
              aria-label="Which document"
              value={leafDocumentId ?? ""}
              onChange={(e) => onLeafSelect?.(e.target.value)}
            >
              <option value="" disabled>
                Select a document…
              </option>
              {groupLeaves(leaves ?? []).map(({ group, items }) => {
                const options = items.map((leaf) => (
                  <option key={leaf.id} value={leaf.id}>
                    {leaf.id} {leaf.title} — {leaf.moduleCount} section{leaf.moduleCount === 1 ? "" : "s"}
                  </option>
                ));
                // A CMC Module 3 offers ~60 leaves; group them under their parent
                // heading (3.2.S.2 Manufacture …) so the list stays scannable.
                return group ? (
                  <optgroup key={`g:${group.id}:${items[0].id}`} label={`${group.id} ${group.title}`}>
                    {options}
                  </optgroup>
                ) : (
                  <React.Fragment key={`u:${items[0].id}`}>{options}</React.Fragment>
                );
              })}
            </select>
          </div>
        )}

        {!awaitingLeaf && (
          <>
            <SearchField value={query} onChange={setQuery} placeholder="Search sections or numbers" />
            {/* Status filter — only once there is something drafted to filter. */}
            {hasDrafted && (
              <Segmented
                label="Filter sections"
                value={activeTab}
                onChange={setTab}
                items={tabDefs.map((t) => ({ id: t.key, label: `${t.label} · ${t.count}` }))}
              />
            )}
            <div className={styles.selectRow}>
              <label className={styles.selectAllLabel}>
                <input
                  type="checkbox"
                  checked={allVisibleOn}
                  disabled={visibleActionable.length === 0}
                  onChange={toggleSelectAll}
                  style={hiddenInput}
                  aria-label="Select all"
                />
                <CheckBox state={allVisibleOn ? "checked" : visibleChecked > 0 ? "some" : "unchecked"} />
                <span aria-live="polite">{`${tabSelected} of ${tabDenominator} selected`}</span>
              </label>
              <span className={styles.selectLinks}>
                {hasDrafted && activeTab !== "done" ? (
                  <button type="button" className={styles.textButton} onClick={selectAllNotDrafted}>
                    Select all not drafted
                  </button>
                ) : (
                  <button
                    type="button"
                    className={styles.textButton}
                    disabled={allVisibleOn || visibleActionable.length === 0}
                    onClick={toggleSelectAll}
                  >
                    Select all
                  </button>
                )}
                <button type="button" className={styles.textButton} disabled={!anySelected} onClick={clearAll}>
                  Clear
                </button>
              </span>
            </div>
          </>
        )}
      </div>

      {/* Until a document is chosen there is nothing meaningful to scope, so the
          section body is replaced by a placeholder — the whole doctype's
          sections must never render as one draftable set. */}
      {awaitingLeaf ? (
        <div className={styles.awaitingLeaf}>Select a document to choose its sections.</div>
      ) : (
        <div className={styles.body}>
          {activeTab === "done" && (
            <div className={styles.redraftNote} role="note">
              Redrafting replaces the section's current text in the document. Review the existing draft before you
              redraft it.
            </div>
          )}
          {groups.length === 0 ? (
            <div className={styles.empty}>
              {query.trim() ? `No sections match "${query.trim()}".` : "No sections here."}
            </div>
          ) : activeTab === "done" ? (
            // Drafted tab: a flat list — the tree context adds nothing here.
            groups
              .flatMap((g) => g.nodes)
              .map((node) => renderRow(node, true))
          ) : (
            groups.map((group, groupIndex) => {
              const key = `${group.gate}:${group.nodes[0]?.n ?? groupIndex}`;
              const displayName = gateLabelByName.get(group.gate) ?? group.gate;
              const isCollapsed = collapsed.has(key) && !query.trim();
              const groupHits = group.nodes.filter((n) => hitIds.has(n.n));
              const groupSelected = groupHits.filter(
                (n) => effectiveCheckedSet.has(n.n) || effectiveImpliedSet.has(n.n),
              ).length;
              return (
                <div key={key} className={styles.group}>
                  {groups.length > 1 || gates.length > 1 ? (
                    <button
                      type="button"
                      className={styles.groupHeader}
                      onClick={() => toggleGroup(key)}
                      aria-expanded={!isCollapsed}
                    >
                      <span className={styles.groupLabel}>{displayName}</span>
                      <span className={styles.groupCount}>{`${groupSelected} of ${groupHits.length}`}</span>
                    </button>
                  ) : null}
                  {!isCollapsed && group.nodes.map((node) => renderRow(node, false))}
                </div>
              );
            })
          )}

        </div>
      )}

      {/* Steering and the document's name and folder stay pinned above the
          footer: a writer drafting the whole document never scrolls the
          section list, and must still see where it will be saved. */}
      {!awaitingLeaf && (
        <div className={styles.extras}>
          {!steerShown && !runInstructions ? (
            <button
              type="button"
              className="as-linkbtn"
              style={{ alignSelf: "flex-start" }}
              onClick={() => {
                setSteerShown(true);
                setSteerOpen(true);
              }}
            >
              + Add steering instructions <span className={styles.optional}>(optional)</span>
            </button>
          ) : (
            <>
              <button
                type="button"
                className={styles.steerHeader}
                onClick={() => setSteerOpen((o) => !o)}
                aria-expanded={steerOpen}
                aria-controls="loom-run-instructions"
              >
                <span style={{ flex: 1 }}>
                  Steering instructions <span className={styles.optional}>(optional)</span>
                </span>
                {!steerOpen && runInstructions.trim() && <span className={styles.optional}>added</span>}
                <span style={{ color: "var(--ink-subtle)", display: "flex" }}>
                  <Chevron dir={steerOpen ? "up" : "down"} />
                </span>
              </button>
              {steerOpen && (
                <textarea
                  id="loom-run-instructions"
                  aria-label="Steering instructions, applied to every selected section"
                  className="as-input"
                  value={runInstructions}
                  maxLength={RUN_INSTRUCTIONS_MAX_CHARS}
                  onChange={(e) => setRunInstructions(e.target.value)}
                  placeholder="e.g. Keep the synopsis under 2 pages and use past tense throughout"
                  rows={2}
                  // biome-ignore lint/a11y/noAutofocus: opened on an explicit click of "+ Add steering instructions"
                  autoFocus={!runInstructions}
                />
              )}
            </>
          )}
          {newDoc && !(editingName || !nameOk || newFolder !== null) && (
            <div className="as-filing">
              <span>
                <span className="as-filing-k">Save as </span>
                <b>{docName.trim()}</b>
                {newDoc.folders && (
                  <span className={styles.saveAsRowFolder}>
                    {" "}
                    · {newDoc.folders.options.find((o) => o.value === folder)?.label ?? "Unfiled"}
                  </span>
                )}
              </span>
              <button
                type="button"
                className="as-linkbtn"
                onClick={() => setEditingName(true)}
                aria-label={`Rename. Saving this document as ${docName.trim()}`}
              >
                Rename
              </button>
            </div>
          )}
          {newDoc && (editingName || !nameOk || newFolder !== null) && (
            <div className={styles.saveAs}>
              <label className={styles.saveAsHead} htmlFor="loom-doc-name">
                <span>Save this document as</span>
                <span className={styles.saveAsHint}>so you can find it on Home</span>
              </label>
              <input
                id="loom-doc-name"
                className="as-input"
                value={docName}
                maxLength={120}
                placeholder="e.g. SNG-208 Phase 1 study report"
                onChange={(e) => setDocName(e.target.value)}
              />
              {newDoc.folders &&
                (newFolder === null ? (
                  <div className={styles.saveAsFolder}>
                    <span>in</span>
                    <select
                      className="as-input as-select"
                      aria-label="Folder"
                      value={folder}
                      onChange={(e) => {
                        if (e.target.value === NEW_FOLDER) setNewFolder("");
                        else setFolder(e.target.value);
                      }}
                    >
                      {newDoc.folders.options.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                      <option value={NEW_FOLDER}>+ New folder…</option>
                    </select>
                  </div>
                ) : (
                  <>
                    <span className={styles.saveAsFolder}>in a new folder</span>
                    <input
                      className="as-input"
                      value={newFolder}
                      maxLength={120}
                      aria-label="New folder name"
                      placeholder="New folder, e.g. SNG-208 Phase 1"
                      onChange={(e) => setNewFolder(e.target.value)}
                      // biome-ignore lint/a11y/noAutofocus: opened by an explicit "+ New folder…" pick
                      autoFocus
                    />
                    <button type="button" className={styles.saveAsLink} onClick={() => setNewFolder(null)}>
                      Choose an existing folder
                    </button>
                  </>
                ))}
              {nameOk && newFolder === null && (
                <button type="button" className={styles.saveAsLink} onClick={() => setEditingName(false)}>
                  Done
                </button>
              )}
            </div>
          )}
          {naming?.mode === "existing" && naming.name && (
            <div className="as-filing">
              <span>
                <span className="as-filing-k">Adding to </span>
                <b>{naming.name}</b>
                {naming.folderLabel && <span className={styles.saveAsRowFolder}> · {naming.folderLabel}</span>}
              </span>
            </div>
          )}
        </div>
      )}

      <FooterBar
        note={
          selectionOk ? (
            <span aria-live="polite">
              About {estMin} min{summaryExtra ? ` · ${summaryExtra}` : ""}. Written into this file as each section
              finishes.
            </span>
          ) : undefined
        }
      >
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleStart} disabled={!canGo}>
          {formatPrimaryLabel(selectionOk ? draftCount : 0, showPlan, strategy, redraftOnly)}
        </Button>
      </FooterBar>

      {/* Overwrite confirmation. Drafted sections are pickable, but re-drafting
          replaces reviewed content and the pane keeps no copy, so a run that
          would do it stops here first. Back returns to the picker with the
          selection intact; Redraft anyway commits it. */}
      {confirmOverwrite && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true" aria-label="Redraft drafted sections?">
          <div className={styles.confirmCard}>
            <div className={styles.confirmTitle}>Redraft drafted sections?</div>
            <div className={styles.confirmBody}>
              {selectedDrafted.length === 1
                ? "1 section you picked is already drafted. Redrafting writes it again from the sources and replaces its current text in the document."
                : `${selectedDrafted.length} sections you picked are already drafted. Redrafting writes them again from the sources and replaces their current text in the document.`}{" "}
              The document is saved first. Tables and figures the new draft leaves out are added back at the end of
              their section.
            </div>
            <div className={styles.confirmButtons}>
              <Button variant="secondary" onClick={() => setConfirmOverwrite(false)}>
                Back to sections
              </Button>
              <Button variant="primary" onClick={handleConfirmOverwrite}>
                Redraft anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoomSelection;

// ---------------------------------------------------------------------------
// PickerRow — extracted as a named component to keep the main
// render tree shallow and to allow React to bail out of row re-
// renders when the row's own inputs don't change. Module-private
// because it's tightly coupled to LoomSelection's styles.
// ---------------------------------------------------------------------------

interface PickerRowProps {
  node: SelectionNode;
  /** Drafted tab: no indent. */
  flat?: boolean;
  checked: boolean;
  implied: boolean;
  /** Implied as a dotted parent — not directly togglable. Other implied
   *  rows are pulled-in deps the writer can untick. */
  impliedFixed?: boolean;
  /** The ticked section that pulled this implied row in. */
  neededBy?: string;
  locked: boolean;
  /** Already drafted (selectable or locked). */
  drafted?: boolean;
  /** Grouping header (owns no instruction). Renders a derived tri-state
   *  checkbox that cascades to its instructed descendants. */
  isHeader?: boolean;
  /** Header: every instructed descendant is checked. */
  headerAll?: boolean;
  /** Header: some (not all) instructed descendants are checked/implied. */
  headerSome?: boolean;
  /** Second line: "accepted 11:37am · 2 issues" or "2 of 3 drafted". */
  subline?: string | null;
  /** Show the plain "Drafted" tag under the title. */
  showDraftedTag?: boolean;
  /** Parent sections and headings are set bold. */
  bold?: boolean;
  /** Shared number-gutter width. */
  numberWidth: string;
  onToggle: (id: string) => void;
}

const PickerRow: React.FC<PickerRowProps> = React.memo(
  ({
    node,
    flat = false,
    checked,
    implied,
    impliedFixed = false,
    neededBy,
    locked,
    drafted = false,
    isHeader = false,
    headerAll = false,
    headerSome = false,
    subline = null,
    showDraftedTag = false,
    bold = false,
    numberWidth,
    onToggle,
  }) => {
    const styles = useStyles();
    // An implied dotted parent is fixed; a pulled-in dep is an ordinary,
    // ticked checkbox.
    const fixed = !isHeader && implied && impliedFixed;
    // Headers derive an all/some/none tri-state from their descendants;
    // draftable rows use their own checked/implied/locked state.
    const state: BoxState = isHeader
      ? headerAll
        ? "checked"
        : headerSome
          ? "some"
          : "unchecked"
      : locked
        ? "locked"
        : checked
          ? "checked"
          : implied
            ? fixed
              ? "implied"
              : "checked"
            : "unchecked";

    // Header: clicking always cascades to its subsections. Draftable
    // rows: an implied parent isn't directly togglable — writer must
    // untick the subsection pulling it in. A pulled-in dep is an ordinary
    // checkbox: unticking it leaves it out.
    const handleClick = (): void => {
      if (locked) return;
      if (isHeader) {
        onToggle(node.n);
        return;
      }
      if (!fixed) onToggle(node.n);
    };

    const selected = !isHeader && checked;
    const indentPx = flat ? 0 : (node.indent || 0) * 18;

    return (
      <label
        className={mergeClasses(styles.row, selected && styles.rowSelected)}
        style={{
          paddingLeft: `${12 + indentPx}px`,
          cursor: locked ? "not-allowed" : fixed ? "help" : "pointer",
          opacity: locked ? 0.55 : 1,
        }}
        title={
          isHeader
            ? "Select every subsection below this heading"
            : locked
              ? "Already drafted"
              : drafted
                ? "Already drafted — ticking it redrafts and replaces it"
                : fixed
                  ? "Included because a subsection is selected"
                  : implied
                    ? `${neededBy ? `Needed by §${neededBy}` : "Needed by a selected section"} — untick to leave it out`
                    : undefined
        }
      >
        <input
          type="checkbox"
          checked={isHeader ? headerAll || headerSome : checked || implied || locked}
          disabled={isHeader ? false : fixed || locked}
          onChange={handleClick}
          style={hiddenInput}
        />
        <CheckBox state={state} />
        <span className={styles.rowNumber} style={{ width: numberWidth, color: "var(--ink-subtle)" }}>
          {node.n}
        </span>
        <span className={styles.rowBody}>
          <span className={styles.rowTitle} style={{ fontWeight: bold ? 600 : 400 }}>
            {node.t}
          </span>
          {(() => {
            // One quiet line: drafted state / progress, plus "dep · needed
            // by §N" when the row rides along with a ticked section.
            const parts = [
              showDraftedTag ? "Drafted" : null,
              subline,
              implied && !isHeader ? formatDepLine(neededBy) : null,
            ].filter(Boolean);
            return parts.length > 0 ? <span className={styles.rowMeta}>{parts.join(" · ")}</span> : null;
          })()}
        </span>
        {/* Headers aren't drafted — no per-row time estimate. */}
        {!isHeader && (
          <span className={styles.rowEst}>{node.size === "l" ? "~8 min" : node.size === "m" ? "~3 min" : "~1 min"}</span>
        )}
      </label>
    );
  },
); // React.memo(PickerRow)
PickerRow.displayName = "PickerRow";
